import { createRouter, RouterProvider } from "@tanstack/react-router";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { CellSignInRequiredError } from "@/lib/cells/cell-session";
import {
  TenantCellUnknownError,
  TenantNotFoundSignedInCellsError,
  isCellRoutingError,
} from "@/lib/cells/tenant-cells";
import { AuthProvider } from "@descope/react-sdk";
import { useEffect, useState } from "react";
import { ThemeProvider } from "./lib/theme/theme-provider";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

// Create a new router instance
const router = createRouter({ routeTree });

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// A request for another cell's tenant can fail because that cell needs its
// own sign-in or couldn't be reached, so say why instead of a bare failure.
const notifyCellRoutingError = (error: unknown) => {
  if (error instanceof CellSignInRequiredError) {
    // A tenant that may simply not exist gets a normal toast; one known to be
    // in the other cell keeps its sign-in action on screen until dismissed.
    const lookupMiss = error instanceof TenantNotFoundSignedInCellsError;
    toast.error(error.message, {
      id: lookupMiss ? error.message : `cell-sign-in-${error.cellId}`,
      duration: lookupMiss ? 10_000 : Infinity,
      closeButton: true,
      action: {
        label: `Sign in to ${error.cellId}`,
        onClick: () => {
          void router.navigate({
            to: "/cell-sign-in",
            search: {
              cell: error.cellId,
              redirect: router.state.location.href,
            },
          });
        },
      },
    });
  } else if (error instanceof TenantCellUnknownError) {
    toast.error(error.message, { id: error.message });
  }
};

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) =>
        !isCellRoutingError(error) && failureCount < 3,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Per-cell queries show their own cell failures next to the data.
      if (!query.meta?.cellFailureShownInline) {
        notifyCellRoutingError(error);
      }
    },
  }),
  mutationCache: new MutationCache({ onError: notifyCellRoutingError }),
});

function App() {
  const [projectId, setProjectId] = useState<string>("");

  useEffect(() => {
    // Get project ID from config or environment
    const getProjectId = () => {
      try {
        const stored = localStorage.getItem("harmony-management-config");
        if (stored) {
          const config = JSON.parse(stored) as { descopeProjectId?: string };
          return (
            config.descopeProjectId ??
            (import.meta.env.VITE_DESCOPE_PROJECT_ID as string | undefined) ??
            ""
          );
        }
      } catch (error) {
        console.error("Failed to load config from localStorage:", error);
      }
      return (
        (import.meta.env.VITE_DESCOPE_PROJECT_ID as string | undefined) ?? ""
      );
    };

    const currentProjectId = getProjectId();

    // If project ID changed from what we have, force reload to reinitialize Descope
    if (projectId && currentProjectId !== projectId) {
      console.log("Project ID changed, reloading to reinitialize Descope SDK");
      window.location.reload();
      return;
    }

    setProjectId(currentProjectId);

    // Listen for config changes
    const handleStorageChange = () => {
      const newProjectId = getProjectId();
      if (newProjectId !== projectId) {
        console.log("Project ID changed via storage, reload page");
        window.location.reload();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [projectId]);

  return (
    <ThemeProvider>
      <AuthProvider projectId={projectId}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
